
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type PrayerChoiceActionInsert = Database['public']['Tables']['prayer_choice_actions']['Insert'];
type PrayerChoiceActionRow = Database['public']['Tables']['prayer_choice_actions']['Row'];

export type ActionType = 'i_need_prayer' | 'i_want_to_pray';
export type ChoiceMethod = 'ai_assistant' | 'manual_prayer';

export interface PrayerChoiceActionData {
  prayerId: string;
  actionType: ActionType;
  choiceMethod: ChoiceMethod;
  metadata?: Record<string, any>;
}

export const PrayerChoiceService = {
  /**
   * Record a prayer choice action
   */
  async recordChoiceAction(data: PrayerChoiceActionData) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const insertData: PrayerChoiceActionInsert = {
      user_id: user.id,
      prayer_id: data.prayerId,
      action_type: data.actionType,
      choice_method: data.choiceMethod,
      metadata: data.metadata || {}
    };

    const { data: result, error } = await supabase
      .from('prayer_choice_actions')
      .insert(insertData)
      .select()
      .single();

    if (error) throw error;
    return result;
  },

  /**
   * Get choice actions for a prayer
   */
  async getChoiceActionsForPrayer(prayerId: string) {
    const { data, error } = await supabase
      .from('prayer_choice_actions')
      .select('*')
      .eq('prayer_id', prayerId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Get user's choice actions
   */
  async getUserChoiceActions(userId: string) {
    const { data, error } = await supabase
      .from('prayer_choice_actions')
      .select(`
        *,
        prayers (
          id,
          title,
          content,
          author_name
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Update prayed_for table with choice action reference
   */
  async recordPrayedFor(prayerId: string, choiceActionId: string, prayerMethod: 'ai_assistant' | 'manual') {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('prayed_for')
      .upsert({
        user_id: user.id,
        prayer_id: prayerId,
        choice_action_id: choiceActionId,
        prayer_method: prayerMethod,
        prayed_at: new Date().toISOString()
      });

    if (error) throw error;
  }
};
